import { PageSpinner } from "@impero-com/ui";
import { usePresetTableSearchParams } from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";

import { TActTableRow } from "api/generated/model";

import {
    buildTestingActivityListTableStateStorageKey,
    testingActivityListColumnFiltersCodecMap,
    testingActivityListDataParamsCodecMap,
    testingActivityListTableDefaultValues,
} from "./consts";
import { useTestingActivitiesforProgramQuery } from "./queries/useTestingActivitiesForProgramQuery";
import { TestingActivityForTestingProgramList } from "./TestingActivityForTestingProgramList";
import { getTestingActivityArchivedFilter } from "./useTestingActivityArchivedFilter";

interface TestingActivityListForTestingProgramViewProps {
    testingProgramId: number;
}
const EMPTY_RESOURCE: TActTableRow[] = [];

export function TestingActivityListForTestingProgramView({
    testingProgramId,
}: TestingActivityListForTestingProgramViewProps) {
    const { t } = useTranslation();

    // Normally this would go in the List component, next to where useXTable is called, but we need to tie the params with the query
    const searchParams = usePresetTableSearchParams(
        buildTestingActivityListTableStateStorageKey(testingProgramId),
        testingActivityListTableDefaultValues,
        testingActivityListColumnFiltersCodecMap,
        t("Table_RemovedFiltersWarning"),
        Sentry.captureException,
        testingActivityListDataParamsCodecMap
    );
    const archivedFilter = getTestingActivityArchivedFilter(
        searchParams.dataParams
    );

    const { data, isFetching, isPending } = useTestingActivitiesforProgramQuery(
        testingProgramId,
        archivedFilter.archivedParam
    );

    // Only the very first load has no data to show; subsequent filter toggles
    // keep the previous rows (placeholderData) and overlay a table spinner.
    if (isPending || !data) {
        return <PageSpinner />;
    }

    // Neither checkbox checked => list nothing, regardless of what the query
    // fetched (the active rows are reused to avoid an extra request).
    const blankedData = {
        ...data,
        content: { ...data.content, resource: EMPTY_RESOURCE },
    };
    const listData = archivedFilter.isEnabled ? data : blankedData;
    const isTableLoading = archivedFilter.isEnabled && isFetching;

    return (
        <TestingActivityForTestingProgramList
            data={listData}
            testingProgramId={testingProgramId}
            archivedFilter={archivedFilter}
            searchParams={searchParams}
            isTableLoading={isTableLoading}
        />
    );
}
